import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import { useMutation, useQuery } from '@tanstack/react-query';

import errorNotification from '../../utilities/errorNotification';
import queryClient from '../../utilities/queryClient';
import hasClockDrift from '../../utilities/hasClockDrift';
import { useRegisterBreadcrumb } from '../layout/navbar/BreadcrumbsManager';
import enrollmentSteps, { deriveEnrollmentStep } from './enrollmentSteps';
import MfaEnrollmentStart from './MfaEnrollmentStart';
import ClockDriftWarning from './ClockDriftWarning';
import MfaEnrollmentVerify from './MfaEnrollmentVerify';
import MfaRecoveryCodes from './MfaRecoveryCodes';
import MfaStepUp, { StepUpCode } from './MfaStepUp';
import stepUpActions, { StepUpAction } from './stepUpActions';

export const MFA_STATUS_ID = 'status';
export const MFA_STATUS_QUERY_KEY = ['auth/mfa', MFA_STATUS_ID];

type PendingEnrollment = {
  id: string;
  secret: string;
  enrollmentUri: string;
  pendingToken: string;
};

type IssuedRecoveryCodes = {
  id: string;
  recoveryCodes: string[];
};

type ConfirmedEnrollment = IssuedRecoveryCodes & { clockDriftSteps?: number };

export type MfaStatus = {
  id: string;
  enabled: boolean;
  required: boolean;
  recoveryCodesRemaining: number;
};

const SecurityPage = () => {
  useRegisterBreadcrumb({ title: 'Security', path: '/security' });

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [stepUpAction, setStepUpAction] = useState<StepUpAction | null>(null);
  const [driftSteps, setDriftSteps] = useState<number | null>(null);

  const {
    data: status,
    isError: statusFailed,
    refetch: reloadStatus,
  } = useQuery({
    queryKey: MFA_STATUS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await dataProvider
        .getOne<MfaStatus>('auth/mfa', { id: MFA_STATUS_ID });
      return data;
    },
  });

  const {
    mutate: startEnrollmentMutate,
    data: pendingEnrollment,
    isPending: startLoading,
    reset: clearPendingEnrollment,
  } = useMutation({
    mutationFn: async (stepUpCode: StepUpCode): Promise<PendingEnrollment> => {
      const { data } = await dataProvider
        .create<PendingEnrollment>('auth/mfa/enroll', { data: stepUpCode });
      return data;
    },
    onError: (error) => {
      notify(errorNotification(error, 'Could not start the setup'), { type: 'error' });
    },
  });

  const startEnrollment = useCallback(() => {
    startEnrollmentMutate({});
  }, [startEnrollmentMutate]);

  const replaceDevice = useCallback(() => {
    setStepUpAction(stepUpActions.REPLACE_DEVICE);
  }, [setStepUpAction]);

  const regenerateRecoveryCodes = useCallback(() => {
    setStepUpAction(stepUpActions.REGENERATE_RECOVERY_CODES);
  }, [setStepUpAction]);

  const startDisable = useCallback(() => {
    setStepUpAction(stepUpActions.DISABLE);
  }, [setStepUpAction]);

  const cancelStepUp = useCallback(() => {
    setStepUpAction(null);
    clearPendingEnrollment();
  }, [setStepUpAction, clearPendingEnrollment]);

  const {
    mutate: disableMfaMutate,
    isPending: disableLoading,
  } = useMutation({
    mutationFn: async (stepUpCode: StepUpCode): Promise<void> => {
      await dataProvider.create('auth/mfa/disable', { data: stepUpCode });
    },
    onSuccess: () => {
      setStepUpAction(null);
      queryClient.setQueryData<MfaStatus>(
        MFA_STATUS_QUERY_KEY,
        (previous) => previous && { ...previous, enabled: false, recoveryCodesRemaining: 0 },
      );
      reloadStatus();
      notify('Multi-factor authentication turned off', { type: 'info' });
    },
    onError: (error) => {
      notify(errorNotification(error, 'Could not turn it off'), { type: 'error' });
    },
  });

  const {
    mutate: regenerateMutate,
    data: regeneratedCodes,
    isPending: regenerateLoading,
    reset: clearRegeneratedCodes,
  } = useMutation({
    mutationFn: async (stepUpCode: StepUpCode): Promise<IssuedRecoveryCodes> => {
      const { data } = await dataProvider
        .create<IssuedRecoveryCodes>('auth/mfa/recovery-codes/regenerate', { data: stepUpCode });
      return data;
    },
    onSuccess: () => {
      reloadStatus();
    },
    onError: (error) => {
      notify(errorNotification(error, 'Could not regenerate the recovery codes'), { type: 'error' });
    },
  });

  const submitStepUp = useCallback((stepUpCode: StepUpCode) => {
    if (stepUpAction === stepUpActions.DISABLE) disableMfaMutate(stepUpCode);
    else if (stepUpAction === stepUpActions.REGENERATE_RECOVERY_CODES) {
      regenerateMutate(stepUpCode);
    } else startEnrollmentMutate(stepUpCode);
  }, [stepUpAction, disableMfaMutate, regenerateMutate, startEnrollmentMutate]);

  const {
    mutate: confirmEnrollmentMutate,
    data: confirmedEnrollment,
    isPending: confirmLoading,
    reset: clearRecoveryCodes,
  } = useMutation({
    mutationFn: async (code: string): Promise<ConfirmedEnrollment> => {
      if (!pendingEnrollment) throw new Error('No pending enrollment');
      const { data } = await dataProvider.create<ConfirmedEnrollment>(
        'auth/mfa/enroll/confirm',
        { data: { pendingToken: pendingEnrollment.pendingToken, code } },
      );
      return data;
    },
    onSuccess: (confirmed) => {
      // The layout's warning has already read the store by now, so the page shows its own.
      setDriftSteps(hasClockDrift(confirmed) ? confirmed.clockDriftSteps : null);
      queryClient.setQueryData<MfaStatus>(
        MFA_STATUS_QUERY_KEY,
        (previous) => previous && {
          ...previous,
          enabled: true,
          recoveryCodesRemaining: confirmed.recoveryCodes.length,
        },
      );
      reloadStatus();
    },
    onError: (error) => {
      notify(errorNotification(error, 'Could not verify the code'), { type: 'error' });
    },
  });

  const confirmEnrollment = useCallback((code: string) => {
    confirmEnrollmentMutate(code);
  }, [confirmEnrollmentMutate]);

  const acknowledgeRecoveryCodes = useCallback(() => {
    clearRecoveryCodes();
    clearRegeneratedCodes();
    clearPendingEnrollment();
    setStepUpAction(null);
  }, [clearRecoveryCodes, clearRegeneratedCodes, clearPendingEnrollment, setStepUpAction]);

  const recoveryCodes = confirmedEnrollment?.recoveryCodes
    ?? regeneratedCodes?.recoveryCodes
    ?? null;
  const enabled = status?.enabled ?? null;
  const step = deriveEnrollmentStep({
    hasRecoveryCodes: !!recoveryCodes,
    hasPendingEnrollment: !!pendingEnrollment,
    isSteppingUp: !!stepUpAction,
  });

  return (
    <Box maxWidth={760}>
      <Typography variant="h5" marginBottom={2}>
        Security
      </Typography>
      <ClockDriftWarning driftSteps={driftSteps} onDismiss={() => setDriftSteps(null)} />
      <Card>
        <CardContent>
          {step === enrollmentSteps.RECOVERY_CODES && recoveryCodes && (
            <MfaRecoveryCodes
              recoveryCodes={recoveryCodes}
              onAcknowledge={acknowledgeRecoveryCodes}
            />
          )}
          {step === enrollmentSteps.PAIR && pendingEnrollment && (
            <MfaEnrollmentVerify
              loading={confirmLoading}
              secret={pendingEnrollment.secret}
              enrollmentUri={pendingEnrollment.enrollmentUri}
              confirmLabel={stepUpAction === stepUpActions.REPLACE_DEVICE ? 'Verify and replace' : 'Verify and enable'}
              onConfirm={confirmEnrollment}
              onCancel={cancelStepUp}
            />
          )}
          {step === enrollmentSteps.STEP_UP && stepUpAction && (
            <MfaStepUp
              loading={startLoading || disableLoading || regenerateLoading}
              action={stepUpAction}
              onSubmit={submitStepUp}
              onCancel={cancelStepUp}
            />
          )}
          {step === enrollmentSteps.STATUS && enabled !== null && (
            <MfaEnrollmentStart
              loading={startLoading}
              enabled={enabled}
              required={status?.required ?? false}
              recoveryCodesRemaining={status?.recoveryCodesRemaining ?? 0}
              onStart={startEnrollment}
              onReplaceDevice={replaceDevice}
              onRegenerateRecoveryCodes={regenerateRecoveryCodes}
              onDisable={startDisable}
            />
          )}
          {step === enrollmentSteps.STATUS && enabled === null && statusFailed && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => reloadStatus()}>Retry</Button>}
            >
              Could not load your security settings.
            </Alert>
          )}
          {step === enrollmentSteps.STATUS && enabled === null && !statusFailed && (
            <CircularProgress size={24} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SecurityPage;
